import { audio } from '../../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../../state/SaveManager.js';
import { applicationLifecycle } from '../../../../shared/lifecycle/index.js';
import { ensureMinimumTextTouchTarget } from '../../../../ui/TouchTarget.js';
import { NUMBER_KINGDOM_ASSETS } from '../../NumberKingdomAssets.js';
import {
  NUMBER_KINGDOM_DISTRICTS,
  completeNumberKingdomDistrictProgress,
  getNumberKingdomState,
  saveNumberKingdomDistrictProgress,
  visitNumberKingdomDistrict,
} from '../../NumberKingdomState.js';
import {
  configureNumberKingdomDistrictRenderer,
  getNumberKingdomDistrictLayout,
} from '../../components/NumberKingdomDistrictLayout.js';
import { createNumberKingdomHomeButton, type NumberKingdomHomeButton } from '../../components/NumberKingdomHomeButton.js';
import { SUBTRACTION_KEEP_ASSETS } from '../subtraction-keep/SubtractionKeepAssets.js';
import {
  COINMAKERS_VAULT_ASSETS,
  COINMAKERS_VAULT_ASSET_LIST,
  canEnterCoinmakersVault,
  getMissingCoinmakersVaultAssets,
} from './CoinmakersVaultAssets.js';
import {
  COINMAKERS_VAULT_CHALLENGES,
  type CoinDenomination,
  type CoinmakersVaultChallengeConfig,
} from './CoinmakersVaultConfig.js';
import {
  CoinmakersVaultState,
  type CoinmakersVaultSerializedState,
} from './CoinmakersVaultState.js';
import { canEnterRoyalChallengeHall } from '../royal-challenge-hall/RoyalChallengeHallAssets.js';

export const COINMAKERS_VAULT_SCENE_KEY = 'CoinmakersVault';
const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';
const DENOMINATIONS: readonly CoinDenomination[] = [1, 5, 10, 25];

interface CoinmakersVaultProgress {
  readonly version: 1;
  readonly challengeIndex: number;
  readonly challengeId: string;
  readonly challengeState: CoinmakersVaultSerializedState;
  readonly completedChallengeIds: readonly string[];
  readonly restored: boolean;
}

interface ArtButton {
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
}

export class CoinmakersVaultScene extends Phaser.Scene {
  static readonly requiredImageKeys = COINMAKERS_VAULT_ASSET_LIST.map(asset => asset.key);

  private challengeIndex = 0;
  private completedChallengeIds = new Set<string>();
  private challenge!: CoinmakersVaultState;
  private dormantBackground!: Phaser.GameObjects.Image;
  private restoredBackground!: Phaser.GameObjects.Image;
  private missionPanel!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private titleText!: Phaser.GameObjects.Text;
  private missionText!: Phaser.GameObjects.Text;
  private targetText!: Phaser.GameObjects.Text;
  private totalText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private controlsText!: Phaser.GameObjects.Text;
  private backText!: Phaser.GameObjects.Text;
  private homeButton!: NumberKingdomHomeButton;
  private nextText!: Phaser.GameObjects.Text;
  private sourceCoins: Phaser.GameObjects.Image[] = [];
  private sourceLabels: Phaser.GameObjects.Text[] = [];
  private placedCoins: Phaser.GameObjects.Image[] = [];
  private buttons: ArtButton[] = [];
  private restored = false;
  private leaving = false;
  private suspended = false;
  private reducedMotion = false;
  private resizeTimer?: number;
  private unsubscribeLifecycle?: () => void;

  constructor() { super({ key: COINMAKERS_VAULT_SCENE_KEY }); }

  create() {
    this.sourceCoins = [];
    this.sourceLabels = [];
    this.placedCoins = [];
    this.buttons = [];
    this.challengeIndex = 0;
    this.completedChallengeIds = new Set<string>();
    this.leaving = false;
    this.suspended = false;

    const world = getNumberKingdomState();
    const missing = getMissingCoinmakersVaultAssets(this.textures);
    if (!world.districtUnlocks.includes(NUMBER_KINGDOM_DISTRICTS.royalMarket)
      || !canEnterCoinmakersVault(this.textures)) {
      if (missing.length > 0) {
        console.error("[CoinmakersVault] Required production textures are missing:",
          missing.map(asset => `${asset.key} (${asset.path})`));
      }
      this.scene.start('ClocktowerPlaza');
      return;
    }

    configureNumberKingdomDistrictRenderer(this);
    void audio.enterSoundscape('number-kingdom');
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.restored = world.districtCompletion[NUMBER_KINGDOM_DISTRICTS.royalMarket] === true;
    this.restoreProgress(world.districtProgress[NUMBER_KINGDOM_DISTRICTS.royalMarket]);
    visitNumberKingdomDistrict(NUMBER_KINGDOM_DISTRICTS.royalMarket);
    SaveManager.setLastScene(COINMAKERS_VAULT_SCENE_KEY);
    SaveManager.save();
    this.buildEnvironment();
    this.buildInterface();
    this.registerInput();
    this.registerLifecycle();
    this.refresh();
    this.cameras.main.fadeIn(this.reducedMotion ? 140 : 420, 255, 225, 170);
  }

  private restoreProgress(value: unknown) {
    const progress = value as Partial<CoinmakersVaultProgress> | undefined;
    if (progress?.version === 1) {
      this.challengeIndex = Phaser.Math.Clamp(
        Number.isInteger(progress.challengeIndex) ? Number(progress.challengeIndex) : 0,
        0,
        COINMAKERS_VAULT_CHALLENGES.length - 1,
      );
      this.completedChallengeIds = new Set(
        (progress.completedChallengeIds ?? []).filter(id =>
          COINMAKERS_VAULT_CHALLENGES.some(config => config.id === id)),
      );
      this.restored ||= progress.restored === true;
      const config = COINMAKERS_VAULT_CHALLENGES[this.challengeIndex];
      const saved = progress.challengeId === config.id ? progress.challengeState : undefined;
      this.challenge = new CoinmakersVaultState(config, saved);
      return;
    }
    this.challenge = new CoinmakersVaultState(COINMAKERS_VAULT_CHALLENGES[0]);
  }

  private buildEnvironment() {
    this.dormantBackground = this.add.image(640, 360, COINMAKERS_VAULT_ASSETS.dormant.key)
      .setDisplaySize(1280, 720).setDepth(0);
    this.restoredBackground = this.add.image(640, 360, COINMAKERS_VAULT_ASSETS.restored.key)
      .setDisplaySize(1280, 720).setDepth(1);
    this.missionPanel = this.add.image(640, 94, SUBTRACTION_KEEP_ASSETS.missionPanel.key).setDepth(40);
    this.cosmo = this.add.image(105, 640, NUMBER_KINGDOM_ASSETS.numberKingdomCosmo.key).setDepth(105);
  }

  private buildInterface() {
    this.titleText = this.makeText("COINMAKER'S VAULT", 28, '#fff2ad', 'bold')
      .setStroke('#2c1746', 7).setDepth(110);
    this.missionText = this.makeText('', 16, '#ffffff', 'bold')
      .setStroke('#233a69', 5).setWordWrapWidth(680).setDepth(110);
    this.targetText = this.makeText('', 25, '#fff2ad', 'bold')
      .setStroke('#26375c', 7).setDepth(110);
    this.totalText = this.makeText('', 18, '#ffffff', 'bold')
      .setStroke('#26375c', 5).setDepth(110);
    this.feedbackText = this.makeText('', 14, '#ffffff', 'bold')
      .setStroke('#26375c', 5).setWordWrapWidth(740).setDepth(110);
    this.controlsText = this.makeText(
      'Tap a source coin to add • tap a placed coin to remove • P/N/D/Q coins • Enter check/next • H hint • R reset',
      10,
      '#fff8db',
      'bold',
    ).setStroke('#26375c', 5).setDepth(110);
    this.backText = this.makeText('←  CLOCKTOWER PLAZA', 14, '#ffffff', 'bold')
      .setStroke('#26375c', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    this.homeButton = createNumberKingdomHomeButton(this, this.returnHome);
    this.nextText = this.makeText('ROYAL CHALLENGE HALL • NEXT DISTRICT', 14, '#fff2ad', 'bold')
      .setStroke('#26375c', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    [this.backText, this.nextText].forEach(target => ensureMinimumTextTouchTarget(target));
    this.backText.on('pointerup', this.returnToClocktower, this);
    this.nextText.on('pointerup', this.travelToRoyalChallengeHall, this);

    DENOMINATIONS.forEach(denomination => {
      const coin = this.add.image(0, 0, this.coinKey(denomination))
        .setDepth(80).setInteractive({ useHandCursor: true }).setData('denomination', denomination);
      coin.on('pointerup', () => this.addCoin(denomination));
      this.sourceCoins.push(coin);
      this.sourceLabels.push(this.makeText('', 14, '#ffffff', 'bold').setStroke('#26375c', 5).setDepth(90));
    });
    this.buttons = [
      this.makeButton('CHECK', () => this.primaryAction()),
      this.makeButton('HINT', () => this.hint()),
      this.makeButton('RESET', () => this.resetChallenge()),
    ];
  }

  private makeText(value: string, size: number, color: string, style: string) {
    return this.add.text(0, 0, value, {
      fontFamily: FONT_UI,
      fontSize: `${size}px`,
      fontStyle: style,
      color,
      align: 'center',
    }).setOrigin(0.5);
  }

  private makeButton(label: string, action: () => void): ArtButton {
    const image = this.add.image(0, 0, SUBTRACTION_KEEP_ASSETS.actionButton.key)
      .setDepth(100).setInteractive({ useHandCursor: true });
    const text = this.makeText(label, 13, '#ffffff', 'bold').setStroke('#26375c', 4).setDepth(101);
    image.on('pointerup', action);
    return { image, label: text };
  }

  private addCoin(denomination: CoinDenomination) {
    if (!this.canAct()) return;
    const result = this.challenge.addCoin(denomination);
    if (result === 'coin-cap') {
      this.feedbackText.setText('The table holds 12 coins. Remove some and build a more efficient combination.');
      audio.wrong();
    } else {
      this.feedbackText.setText(`Added ${denomination}¢. The running total updates immediately.`);
      audio.click();
    }
    this.capture();
  }

  private removeCoin(denomination: CoinDenomination) {
    if (!this.canAct() || this.challenge.removeCoin(denomination) !== 'accepted') return;
    this.feedbackText.setText(`Removed ${denomination}¢. Keep revising until the payment fits.`);
    audio.click();
    this.capture();
  }

  private primaryAction() {
    if (this.restored) return this.travelToRoyalChallengeHall();
    if (this.challenge.snapshot.complete) return this.startNextChallenge();
    const result = this.challenge.check();
    if (result === 'wrong-total') {
      const remaining = this.challenge.snapshot.remainingCents;
      this.feedbackText.setText(remaining > 0
        ? `The payment is ${remaining}¢ short. Add or exchange coins, then check again.`
        : `The payment is ${Math.abs(remaining)}¢ over. Remove coins and revise it.`);
      audio.wrong();
      return this.capture();
    }
    if (result === 'too-many-coins') {
      this.feedbackText.setText(`The total is right, but use ${this.currentConfig.coinLimit} coins or fewer. Exchange for larger values.`);
      audio.wrong();
      return this.capture();
    }
    const config = this.currentConfig;
    this.completedChallengeIds.add(config.id);
    SaveManager.recordAnswer(config.skillId, true);
    this.feedbackText.setText(`${config.targetCents}¢ accepted! The mint mechanism powers on.`);
    audio.correct();
    if (this.completedChallengeIds.size === COINMAKERS_VAULT_CHALLENGES.length) this.completeDistrict();
    else {
      this.buttons[0].label.setText('NEXT PAYMENT');
      this.playMagicBurst(640, 390, 320);
    }
    this.capture();
  }

  private startNextChallenge() {
    const nextIndex = COINMAKERS_VAULT_CHALLENGES.findIndex(
      config => !this.completedChallengeIds.has(config.id),
    );
    if (nextIndex < 0) return this.completeDistrict();
    this.challengeIndex = nextIndex;
    this.challenge = new CoinmakersVaultState(this.currentConfig);
    this.feedbackText.setText('A new payment seal appears on the counting table.');
    this.buttons[0].label.setText('CHECK');
    this.capture();
  }

  private completeDistrict() {
    this.restored = true;
    this.completedChallengeIds = new Set(COINMAKERS_VAULT_CHALLENGES.map(config => config.id));
    completeNumberKingdomDistrictProgress(
      NUMBER_KINGDOM_DISTRICTS.royalMarket,
      this.progressSnapshot(),
      NUMBER_KINGDOM_DISTRICTS.masteryKeep,
    );
    this.feedbackText.setText('Every seal is paid exactly. The vault opens to Royal Challenge Hall!');
    this.playMagicBurst(640, 360, 560);
    audio.restoration();
  }

  private hint() {
    if (!this.canAct()) return;
    this.feedbackText.setText(this.challenge.requestHint() ?? 'Try the largest useful coin first.');
    audio.click();
    this.capture();
  }

  private resetChallenge() {
    if (!this.canAct()) return;
    this.challenge = new CoinmakersVaultState(this.currentConfig);
    this.feedbackText.setText('This payment is reset. Completed vault seals remain safe.');
    audio.click();
    this.capture();
  }

  private capture() {
    saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.royalMarket, this.progressSnapshot());
    this.refresh();
  }

  private progressSnapshot(): CoinmakersVaultProgress {
    return {
      version: 1,
      challengeIndex: this.challengeIndex,
      challengeId: this.currentConfig.id,
      challengeState: this.challenge.serialize(),
      completedChallengeIds: [...this.completedChallengeIds],
      restored: this.restored,
    };
  }

  private refresh() {
    const snapshot = this.challenge.snapshot;
    this.restoredBackground.setAlpha(this.restored ? 1 : 0);
    this.dormantBackground.setAlpha(this.restored ? 0 : 1);
    this.missionText.setText(this.restored
      ? 'The vault is restored. The open door leads to Royal Challenge Hall.'
      : `Seal ${this.challengeIndex + 1} of ${COINMAKERS_VAULT_CHALLENGES.length}: ${this.currentConfig.prompt}`);
    this.targetText.setText(this.restored ? 'VAULT RESTORED' : `TARGET  ${this.currentConfig.targetCents}¢`);
    this.totalText.setText(this.restored
      ? 'Every mint mechanism is powered.'
      : `YOUR TOTAL  ${snapshot.totalCents}¢  •  ${snapshot.coinCount}/${this.currentConfig.coinLimit} coins`);
    this.sourceCoins.forEach((coin, index) => coin.setVisible(!this.restored));
    this.sourceLabels.forEach((label, index) => {
      const denomination = DENOMINATIONS[index];
      label.setText(`${denomination}¢  ×${snapshot.coins[denomination]}`).setVisible(!this.restored);
    });
    this.buttons.forEach(button => {
      button.image.setVisible(!this.restored);
      button.label.setVisible(!this.restored);
    });
    this.buttons[0].label.setText(snapshot.complete ? 'NEXT PAYMENT' : 'CHECK');
    this.controlsText.setVisible(!this.restored);
    this.nextText.setVisible(this.restored);
    if (!this.feedbackText.text) this.feedbackText.setText('Build the target amount. Every choice can be revised.');
    this.renderPlacedCoins();
    this.layout();
  }

  private renderPlacedCoins() {
    this.placedCoins.forEach(coin => coin.destroy());
    this.placedCoins = [];
    if (this.restored) return;
    DENOMINATIONS.forEach(denomination => {
      for (let index = 0; index < this.challenge.snapshot.coins[denomination]; index += 1) {
        const coin = this.add.image(0, 0, this.coinKey(denomination))
          .setDepth(75).setInteractive({ useHandCursor: true }).setData('denomination', denomination);
        coin.on('pointerup', () => this.removeCoin(denomination));
        this.placedCoins.push(coin);
      }
    });
  }

  private layout() {
    const { safe, compact } = getNumberKingdomDistrictLayout(this);
    const portrait = safe.width < 700;
    const panelWidth = portrait ? safe.width - 30 : Math.min(compact ? 650 : 740, safe.width - 230);
    this.missionPanel.setPosition(safe.centerX, safe.y + (portrait ? 126 : compact ? 82 : 92))
      .setDisplaySize(panelWidth, panelWidth * (360 / 900));
    this.titleText.setPosition(safe.centerX, safe.y + (portrait ? 58 : 25))
      .setFontSize(portrait ? 21 : compact ? 22 : 28);
    this.missionText.setPosition(safe.centerX, safe.y + (portrait ? 132 : compact ? 87 : 98))
      .setFontSize(portrait ? 12 : compact ? 13 : 16).setWordWrapWidth(panelWidth * 0.8);
    this.backText.setPosition(safe.x + (portrait ? 238 : compact ? 250 : 282), safe.y + 30)
      .setFontSize(portrait ? 9 : compact ? 11 : 14);
    this.homeButton.layout(safe, compact || portrait, 'left');
    this.targetText.setPosition(safe.centerX, safe.y + (portrait ? 220 : compact ? 174 : 190))
      .setFontSize(portrait ? 20 : compact ? 22 : 25);
    this.totalText.setPosition(safe.centerX, safe.y + (portrait ? 252 : compact ? 208 : 228))
      .setFontSize(portrait ? 13 : compact ? 15 : 18);

    const placedSize = portrait ? 54 : compact ? 62 : 70;
    const columns = portrait ? 4 : 6;
    this.placedCoins.forEach((coin, index) => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const rowCount = Math.min(columns, this.placedCoins.length - row * columns);
      coin.setPosition(
        safe.centerX + (column - (rowCount - 1) / 2) * (placedSize + 10),
        safe.y + (portrait ? 335 : compact ? 320 : 340) + row * (placedSize + 8),
      ).setDisplaySize(placedSize, placedSize);
    });

    const sourceY = safe.bottom - (compact ? 170 : 188);
    const sourceGap = portrait ? 90 : compact ? 115 : 130;
    const sourceSizes = portrait ? [58, 68, 52, 76] : compact ? [68, 82, 60, 90] : [76, 90, 66, 98];
    this.sourceCoins.forEach((coin, index) => {
      const x = safe.centerX + (index - 1.5) * sourceGap;
      coin.setPosition(x, sourceY).setDisplaySize(sourceSizes[index], sourceSizes[index]);
      this.sourceLabels[index].setPosition(x, sourceY + (portrait ? 48 : 58))
        .setFontSize(portrait ? 11 : compact ? 12 : 14);
    });
    const buttonY = safe.bottom - (compact ? 66 : 72);
    const buttonGap = portrait ? Math.min(145, (safe.width - 120) / 2) : compact ? 150 : 170;
    this.buttons.forEach((button, index) => {
      const x = safe.centerX + (index - 1) * buttonGap;
      button.image.setPosition(x, buttonY)
        .setDisplaySize(portrait ? 110 : compact ? 130 : 145, portrait ? 54 : compact ? 64 : 72);
      button.label.setPosition(x, buttonY).setFontSize(portrait ? 10 : compact ? 12 : 13);
    });
    this.feedbackText.setPosition(safe.centerX, sourceY - (portrait ? 62 : compact ? 68 : 76))
      .setFontSize(portrait ? 11 : compact ? 12 : 14)
      .setWordWrapWidth(portrait ? safe.width - 45 : Math.min(740, safe.width - 230));
    this.controlsText.setPosition(safe.centerX, safe.bottom - 17)
      .setFontSize(portrait ? 7 : compact ? 9 : 10).setWordWrapWidth(portrait ? safe.width - 30 : safe.width);
    this.nextText.setPosition(safe.centerX, safe.bottom - 42).setFontSize(portrait ? 11 : 14);
    this.cosmo.setPosition(safe.x + (compact ? 56 : 72), safe.bottom - (compact ? 48 : 58))
      .setDisplaySize(compact ? 84 : 104, compact ? 76 : 93);
  }

  private coinKey(denomination: CoinDenomination) {
    if (denomination === 1) return COINMAKERS_VAULT_ASSETS.oneCent.key;
    if (denomination === 5) return COINMAKERS_VAULT_ASSETS.fiveCent.key;
    if (denomination === 10) return COINMAKERS_VAULT_ASSETS.tenCent.key;
    return COINMAKERS_VAULT_ASSETS.twentyFiveCent.key;
  }

  private playMagicBurst(x: number, y: number, size: number) {
    const burst = this.add.image(x, y, SUBTRACTION_KEEP_ASSETS.magicBurst.key)
      .setDisplaySize(size, size).setDepth(95).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.68 : 0);
    if (this.reducedMotion) this.time.delayedCall(650, () => burst.destroy());
    else this.tweens.add({ targets: burst, alpha: { from: 0, to: 0.9 }, scaleX: 1.12, scaleY: 1.12,
      duration: 500, yoyo: true, hold: 120, onComplete: () => burst.destroy() });
  }

  private registerInput() {
    this.input.keyboard?.on('keydown', this.handleKeyDown, this);
  }

  private handleKeyDown(event: KeyboardEvent) {
    const key = event.key.toLocaleLowerCase();
    if (key === 'escape') return this.returnToClocktower();
    if (key === 'm') return this.returnHome();
    if (key === 'enter' || key === ' ') return this.primaryAction();
    if (key === 'h') return this.hint();
    if (key === 'r') return this.resetChallenge();
    if (key === 'p' || key === '1') return this.addCoin(1);
    if (key === 'n' || key === '5') return this.addCoin(5);
    if (key === 'd' || key === '0') return this.addCoin(10);
    if (key === 'q' || key === '2') this.addCoin(25);
  }

  private registerLifecycle() {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => { this.suspended = true; },
      onResume: () => { this.suspended = false; },
    });
    window.addEventListener('resize', this.handleResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  private get currentConfig(): CoinmakersVaultChallengeConfig {
    return COINMAKERS_VAULT_CHALLENGES[this.challengeIndex];
  }

  private canAct() {
    return !this.leaving && !this.suspended && !this.restored && !this.challenge.snapshot.complete;
  }

  private readonly handleResize = () => {
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      configureNumberKingdomDistrictRenderer(this);
      this.layout();
    }, 80);
  };

  private readonly returnToClocktower = () => {
    if (this.leaving) return;
    this.leaving = true;
    saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.royalMarket, this.progressSnapshot());
    this.scene.start('ClocktowerPlaza');
  };

  private readonly returnHome = () => {
    if (this.leaving) return;
    this.leaving = true;
    saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.royalMarket, this.progressSnapshot());
    SaveManager.setLastScene(COINMAKERS_VAULT_SCENE_KEY);
    SaveManager.save();
    this.scene.start('HomeHub');
  };

  private readonly travelToRoyalChallengeHall = () => {
    if (!this.restored || this.leaving) return;
    if (!canEnterRoyalChallengeHall(this.textures)) {
      this.feedbackText.setText('The royal route is open. The Challenge Hall is awaiting its runtime QA gate.');
      return audio.click();
    }
    this.leaving = true;
    saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.royalMarket, this.progressSnapshot());
    this.scene.start('RoyalChallengeHall');
  };

  private shutdown() {
    if (this.challenge) {
      saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.royalMarket, this.progressSnapshot());
    }
    this.unsubscribeLifecycle?.();
    audio.leaveSoundscape({ id: 'number-kingdom' });
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.input.keyboard?.off('keydown', this.handleKeyDown, this);
    this.placedCoins.forEach(coin => coin.destroy());
    this.tweens.killAll();
  }
}
